/**
 * Paper-clerk keyboard flow (plan §11 / BidWestern Phase 8.5): the clerk
 * never leaves the keyboard during a lot.
 *
 *   digits            → amount being typed (or, after Tab, the paddle)
 *   Tab               → amount done, now the paddle
 *   Enter             → with amount + paddle: FLOOR BID at that amount from
 *                       that paddle; with nothing typed and a high bid on the
 *                       board: HAMMER; with nothing typed and no bid: ignored
 *   Backspace         → edit the active field
 *   Escape            → clear both fields
 *   + / -             → ask one increment up / down (no bid placed)
 *   Space             → FAIR WARNING ("going once") when bidding
 *   F                 → floor bid AT THE CURRENT ASK from the typed paddle
 *                       (the common case: "yep, 4821" - one key, one paddle)
 *
 * Pure: (state, key, context) → (state, intent). The DOM layer renders state
 * and dispatches intents to the ring. Testable without a browser.
 */

export interface KeyState {
  readonly amount: string; // digits as typed (dollars)
  readonly paddle: string;
  readonly field: 'amount' | 'paddle';
}

export const IDLE: KeyState = { amount: '', paddle: '', field: 'amount' };

export interface KeyContext {
  /** Is a lot in a bidding state (OPEN / FAIR_WARNING)? */
  readonly bidding: boolean;
  readonly currentAsk: number | null;
  readonly currentHighBid: number | null;
  /** One increment at the current ask, for +/-. */
  readonly increment: number;
}

export type KeyIntent =
  | { type: 'none' }
  | { type: 'floor_bid'; amount: number; paddle: string }
  | { type: 'hammer' }
  | { type: 'fair_warning' }
  | { type: 'set_ask'; ask: number }
  | { type: 'error'; message: string };

const DIGIT = /^[0-9]$/;

export function keyReducer(state: KeyState, key: string, ctx: KeyContext): { state: KeyState; intent: KeyIntent } {
  const none = (s: KeyState) => ({ state: s, intent: { type: 'none' } as const });

  if (DIGIT.test(key)) {
    const v = state.field === 'amount' ? state.amount : state.paddle;
    if (v.length >= 9) return none(state); // nobody bids a billion on a horse
    return none(state.field === 'amount' ? { ...state, amount: v + key } : { ...state, paddle: v + key });
  }
  switch (key) {
    case 'Backspace': {
      if (state.field === 'paddle') {
        if (state.paddle) return none({ ...state, paddle: state.paddle.slice(0, -1) });
        return none({ ...state, field: 'amount' }); // back into the amount
      }
      return none({ ...state, amount: state.amount.slice(0, -1) });
    }
    case 'Escape':
      return none(IDLE);
    case 'Tab':
      if (state.field === 'amount' && state.amount) return none({ ...state, field: 'paddle' });
      if (state.field === 'amount' && !state.amount && ctx.currentAsk !== null) {
        // Tab with nothing typed = "at the ask"; jump to paddle.
        return none({ ...state, amount: String(ctx.currentAsk), field: 'paddle' });
      }
      return none(state);
    case 'Enter': {
      if (!state.amount && !state.paddle) {
        if (!ctx.bidding) return none(state);
        if (ctx.currentHighBid === null) return { state, intent: { type: 'error', message: 'no bid to hammer' } };
        return { state: IDLE, intent: { type: 'hammer' } };
      }
      if (!ctx.bidding) return { state, intent: { type: 'error', message: 'lot is not open for bidding' } };
      const amount = Number(state.amount);
      if (!state.amount || !Number.isFinite(amount) || amount <= 0) return { state, intent: { type: 'error', message: 'amount first, then Tab, then paddle' } };
      if (!state.paddle) return { state, intent: { type: 'error', message: 'paddle number required (Tab to the paddle field)' } };
      // The high bid only goes up (engine invariant 7); say so before the round trip.
      if (ctx.currentHighBid !== null && amount <= ctx.currentHighBid) return { state, intent: { type: 'error', message: `$${amount.toLocaleString()} does not beat the high bid $${ctx.currentHighBid.toLocaleString()}` } };
      return { state: IDLE, intent: { type: 'floor_bid', amount, paddle: state.paddle } };
    }
    case 'f':
    case 'F': {
      // Floor bid at the current ask from the typed paddle.
      if (!ctx.bidding || ctx.currentAsk === null) return { state, intent: { type: 'error', message: 'lot is not open for bidding' } };
      const paddle = state.field === 'paddle' ? state.paddle : state.amount; // whatever they typed is the paddle
      if (!paddle) return { state, intent: { type: 'error', message: 'type the paddle, then F' } };
      return { state: IDLE, intent: { type: 'floor_bid', amount: ctx.currentAsk, paddle } };
    }
    case ' ':
      if (!ctx.bidding) return none(state);
      return { state, intent: { type: 'fair_warning' } };
    case '+':
    case '=':
      if (!ctx.bidding || ctx.currentAsk === null) return none(state);
      return { state, intent: { type: 'set_ask', ask: ctx.currentAsk + ctx.increment } };
    case '-':
      if (!ctx.bidding || ctx.currentAsk === null) return none(state);
      return { state, intent: { type: 'set_ask', ask: Math.max(ctx.increment, ctx.currentAsk - ctx.increment) } };
    default:
      return none(state);
  }
}

/** What the strip should display. */
export function keyDisplay(state: KeyState, ctx: KeyContext): { amount: string; paddle: string; hint: string } {
  const amount = state.amount ? '$' + Number(state.amount).toLocaleString() : ctx.currentAsk !== null ? `at ask $${ctx.currentAsk.toLocaleString()}` : '-';
  const paddle = state.paddle || '-';
  const hint = !ctx.bidding
    ? 'open a lot to bid'
    : state.field === 'amount' && !state.amount
      ? 'type amount → Tab → paddle → Enter · or paddle → F (at ask) · Enter alone = HAMMER'
      : state.field === 'amount'
        ? 'Tab → paddle'
        : 'paddle → Enter';
  return { amount, paddle, hint };
}
